import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './i18n'; // Initialize i18next

import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';

import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { Spinner } from './components/LoadingSkeletons/Spinner';
import Home from './pages/Home';
import Tuning from './pages/Tuning';
import Workshop from './pages/Workshop';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import TuningPoints from './pages/TuningPoints';
import Partnership from './pages/Partnership';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import Orders from './pages/Orders';
import AdminOrders from './pages/AdminOrders';
import AdminContactMessages from './pages/AdminContactMessages';
import AdminPartnershipRequests from './pages/AdminPartnershipRequests';
import AdminNewsletter from './pages/AdminNewsletter';
import AdminEcuTuning from './pages/AdminEcuTuning';
import AdminInstagram from './pages/AdminInstagram';
import NewsletterUnsubscribe from './pages/NewsletterUnsubscribe';
import NotFound from './pages/NotFound';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import PublicRoute from './components/PublicRoute';

function App() {
  return (
    <HelmetProvider>
      <Suspense fallback={
        <div className="min-h-screen flex flex-col items-center justify-center bg-white">
          <img
            src="/logo-dark.png"
            alt="Meller Racing"
            className="h-14 w-auto animate-pulse"
          />
          <div className="mt-5">
            <Spinner size="md" color="red" />
          </div>
        </div>
      }>
        <LanguageProvider>
          <AuthProvider>
            <CartProvider>
              <ErrorBoundary>
                <Router>
                  <ScrollToTop />
                  <div className="flex flex-col min-h-screen">
                    <Navbar />
                    <main className="flex-grow">
                      <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/tuning" element={<Tuning />} />
                    <Route path="/tuning/:slug" element={<ProductDetail />} />
                    <Route path="/workshop" element={<Workshop />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/products/:slug" element={
                      <AdminRoute>
                        <ProductDetail />
                      </AdminRoute>
                    } />
                    <Route path="/tuning-points" element={<TuningPoints />} />
                    <Route path="/partnership" element={<Partnership />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/login" element={
                      <PublicRoute>
                        <Login />
                      </PublicRoute>
                    } />
                    <Route path="/register" element={
                      <PublicRoute>
                        <Register />
                      </PublicRoute>
                    } />
                    <Route path="/forgot-password" element={
                      <PublicRoute>
                        <ForgotPassword />
                      </PublicRoute>
                    } />
                    <Route path="/reset-password" element={
                      <PublicRoute>
                        <ResetPassword />
                      </PublicRoute>
                    } />
                    <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribe />} />
                    
                    {/* Protected Routes */}
                    <Route path="/cart" element={
                      <PrivateRoute>
                        <Cart />
                      </PrivateRoute>
                    } />
                    <Route path="/checkout" element={
                      <PrivateRoute>
                        <Checkout />
                      </PrivateRoute>
                    } />
                    <Route path="/profile" element={
                      <PrivateRoute>
                        <Profile />
                      </PrivateRoute>
                    } />
                    <Route path="/orders" element={
                      <PrivateRoute>
                        <Orders />
                      </PrivateRoute>
                    } />
                    
                    {/* Admin Routes */}
                    <Route path="/admin/orders" element={
                      <AdminRoute>
                        <AdminOrders />
                      </AdminRoute>
                    } />
                    <Route path="/admin/contact-messages" element={
                      <AdminRoute>
                        <AdminContactMessages />
                      </AdminRoute>
                    } />
                    <Route path="/admin/partnership-requests" element={
                      <AdminRoute>
                        <AdminPartnershipRequests />
                      </AdminRoute>
                    } />
                    <Route path="/admin/newsletter" element={
                      <AdminRoute>
                        <AdminNewsletter />
                      </AdminRoute>
                    } />
                    <Route path="/admin/ecu-tuning" element={
                      <AdminRoute>
                        <AdminEcuTuning />
                      </AdminRoute>
                    } />
                    <Route path="/admin/instagram" element={
                      <AdminRoute>
                        <AdminInstagram />
                      </AdminRoute>
                    } />
                    
                    {/* 404 Not Found - Catch all unmatched routes */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
                <Footer />
              </div>
              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
            </Router>
            </ErrorBoundary>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
      </Suspense>
    </HelmetProvider>
  );
}

export default App;
